import { X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import type { AddressComboboxProps } from './address-suggestions'

/** Existing API keeps RFC mailbox strings; pills are an editing aid only. */
export function AddressPills({
  id,
  value,
  onChange,
  combobox,
}: {
  id: string
  value: string
  onChange: (value: string) => void
  /** Optional suggestion list wiring (#820); without it nothing changes. */
  combobox?: AddressComboboxProps
}) {
  // Typing must never swap the input away mid-word: once the field is focused
  // or edited it stays a text input until blur/Enter turns the text into pills.
  const [editing, setEditing] = useState(false)
  const input = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (editing) input.current?.focus()
  }, [editing])
  const addresses = [
    ...new Set(
      value
        .split(/[,;](?=(?:[^"]*"[^"]*")*[^"]*$)/)
        .map((a) => a.trim())
        .filter(Boolean),
    ),
  ]
  if (editing || !addresses.length)
    return (
      <input
        {...combobox}
        id={id}
        value={value}
        ref={input}
        onFocus={() => {
          setEditing(true)
          combobox?.onFocus()
        }}
        onChange={(e) => {
          setEditing(true)
          onChange(e.target.value)
        }}
        onBlur={() => {
          combobox?.onBlur()
          setEditing(false)
        }}
        onKeyDown={(e) => {
          combobox?.onKeyDown(e)
          // An open suggestion list took Enter/Esc/arrows.
          if (e.defaultPrevented) return
          if (e.key === 'Enter') {
            e.preventDefault()
            // The input unmounts without a reliable blur (WebKit): close the list too.
            combobox?.onBlur()
            setEditing(false)
          }
        }}
        placeholder="name@beispiel.de"
        className="min-h-11 w-full bg-transparent text-base outline-none"
      />
    )
  return (
    <div className="flex min-h-11 flex-wrap items-center gap-1.5">
      {addresses.map((address, i) => (
        <span
          key={address}
          className="inline-flex max-w-full items-center gap-1 rounded-full border border-border-subtle bg-bg-overlay px-2 py-1 text-xs"
        >
          <button
            id={i === 0 ? id : undefined}
            type="button"
            className="truncate min-h-7"
            onClick={() => setEditing(true)}
          >
            {address.replace(/\s*<.*>/, '')}
          </button>
          <button
            type="button"
            aria-label={`${address} entfernen`}
            className="min-h-7 min-w-7"
            onClick={() => onChange(addresses.filter((_, index) => index !== i).join(', '))}
          >
            <X size={14} />
          </button>
        </span>
      ))}
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="min-h-11 min-w-11 text-text-tertiary"
        aria-label="Adressen bearbeiten"
      >
        +
      </button>
    </div>
  )
}
